import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Target, Settings, Users, Rocket, Loader2, CheckCircle2, ChevronRight, ChevronLeft, SkipForward } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import RolePicker, { DEFAULT_ROLES, INITIALLY_SELECTED } from '../components/RolePicker';

const API = process.env.REACT_APP_BACKEND_URL;

const INDUSTRIES = [
  { id: 'bygg', name: 'Bygg og anlegg' },
  { id: 'renhold', name: 'Renhold' },
  { id: 'transport', name: 'Transport' },
  { id: 'industri', name: 'Industri/Produksjon' },
  { id: 'helse', name: 'Helse og omsorg' },
  { id: 'restaurant', name: 'Servering/Restaurant' },
  { id: 'kontor', name: 'Kontor/Administrasjon' },
  { id: 'handel', name: 'Handel/Butikk' },
  { id: 'it', name: 'IT og teknologi' },
  { id: 'eiendom', name: 'Eiendom/Forvaltning' },
  { id: 'barnehage', name: 'Barnehage/Skole' },
  { id: 'verksted', name: 'Verksted/Bil' },
  { id: 'annet', name: 'Annet' },
];

const ISO_STANDARDS = [
  { id: 'iso_9001', name: 'ISO 9001', desc: 'Kvalitetsledelse' },
  { id: 'iso_14001', name: 'ISO 14001', desc: 'Miljøledelse' },
  { id: 'iso_45001', name: 'ISO 45001', desc: 'HMS-ledelse' },
];

const MF_BRANSJER = [
  { id: 'kontor', name: 'Kontor og administrasjon' },
  { id: 'butikk_handel', name: 'Butikk og handel' },
  { id: 'hotell', name: 'Hotell og overnatting' },
  { id: 'restaurant_servering', name: 'Restaurant og servering' },
  { id: 'bygg_anlegg', name: 'Bygg og anlegg' },
  { id: 'eiendom_forvaltning', name: 'Eiendom og forvaltning' },
  { id: 'transport_logistikk', name: 'Transport og logistikk' },
  { id: 'industri', name: 'Industri og produksjon' },
  { id: 'helse_omsorg', name: 'Helse og omsorg' },
  { id: 'barnehage_skole', name: 'Barnehage og skole' },
  { id: 'verksted_bil', name: 'Verksted og bilbransjen' },
  { id: 'renhold', name: 'Renholdsbedrift' },
  { id: 'idrett_kultur', name: 'Idrett og kultur' },
  { id: 'annet_mf', name: 'Annet' },
];

const STEPS = [
  { id: 'welcome', icon: Building2, title: 'Velkommen' },
  { id: 'goal', icon: Target, title: 'Mål' },
  { id: 'details', icon: Settings, title: 'Detaljer' },
  { id: 'roles', icon: Users, title: 'Roller' },
  { id: 'generate', icon: Rocket, title: 'Generer' },
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [industry, setIndustry] = useState('');
  const [mode, setMode] = useState('');
  const [isoStandards, setIsoStandards] = useState(['iso_9001', 'iso_14001', 'iso_45001']);
  const [mfBransje, setMfBransje] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([...INITIALLY_SELECTED]);
  const [employees, setEmployees] = useState('');
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);

  const toggleIso = (id) => setIsoStandards(p => p.includes(id) ? p.filter(s => s !== id) : [...p, id]);

  const canProceed = () => {
    if (step === 0) return !!industry;
    if (step === 1) return !!mode;
    if (step === 2) {
      if (mode === 'iso') return isoStandards.length > 0;
      if (mode === 'miljofyrtarn') return !!mfBransje;
      return true;
    }
    if (step === 3) return selectedRoles.length > 0;
    return true;
  };

  const skipOnboarding = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.post(`${API}/api/v2/auth/complete-onboarding`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) { /* ignore */ }
    navigate('/dashboard');
  };

  const startGeneration = useCallback(async () => {
    setGenerating(true);
    setProgress(5);
    const token = localStorage.getItem('systemklart_token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      // Get user's org
      const userRes = await axios.get(`${API}/api/v2/auth/me`, { headers });
      const orgId = userRes.data.org_id;
      setProgress(15);

      // Build generation input
      const input = {
        roller: selectedRoles.map(id => {
          const role = DEFAULT_ROLES.find(r => r.id === id);
          return role ? role.name : id.replace('custom_', '').replace(/_/g, ' ');
        }),
        risiko_faktorer: [],
        tekniske_anlegg: [],
        spesialkrav: [],
        mode: mode,
        sertifiseringer: mode === 'iso' ? isoStandards : mode === 'miljofyrtarn' ? ['miljofyrtarn'] : [],
        mf_bransje: mode === 'miljofyrtarn' ? mfBransje : null,
        antall_ansatte: parseInt(employees) || 10,
      };

      setProgress(25);

      // Generate documents
      const genRes = await axios.post(
        `${API}/api/v2/ik/organizations/${orgId}/generate-ik-system`,
        { industry, ...input },
        { headers, timeout: 300000 }
      );

      setProgress(90);

      // Mark onboarding complete
      await axios.post(`${API}/api/v2/auth/complete-onboarding`, {}, { headers });

      setProgress(100);
      setDone(true);
      toast.success('Dokumenter generert!');
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Generering feilet. Du kan prøve igjen fra dashboardet.');
      // Still mark onboarding as done so user isn't stuck
      try {
        await axios.post(`${API}/api/v2/auth/complete-onboarding`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) { /* ignore */ }
      setGenerating(false);
    }
  }, [industry, mode, isoStandards, mfBransje, selectedRoles, employees, navigate]);

  const modeLabels = {
    internkontroll: 'IK §5 Internkontroll',
    iso: `ISO-klargjøring (${isoStandards.map(s => s.replace('iso_', '')).join('/')})`,
    miljofyrtarn: 'Miljøfyrtårn-klargjøring',
  };

  // Step indicator
  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-1 mb-8" data-testid="onboarding-steps">
      {STEPS.map((s, i) => (
        <div key={s.id} className="flex items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
            i < step ? 'bg-emerald-500 text-white' :
            i === step ? 'bg-cyan-500 text-white ring-4 ring-cyan-500/20' :
            'bg-slate-700 text-slate-400'
          }`}>
            {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
          </div>
          {i < STEPS.length - 1 && (
            <div className={`w-8 h-0.5 ${i < step ? 'bg-emerald-500' : 'bg-slate-700'}`} />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <StepIndicator />

        <div className="bg-slate-900/80 backdrop-blur border border-slate-800/60 rounded-2xl p-8" data-testid="onboarding-wizard">

          {/* ── Step 0: Welcome + Industry ── */}
          {step === 0 && (
            <div data-testid="onboarding-step-welcome">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-cyan-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-cyan-400" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Velkommen til Systemklar</h1>
                <p className="text-slate-400">Vi setter opp systemet ditt med noen enkle valg.</p>
              </div>

              <h2 className="text-sm font-semibold text-slate-300 mb-3">Hvilken bransje er dere i?</h2>
              <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                {INDUSTRIES.map(ind => (
                  <button
                    key={ind.id}
                    onClick={() => setIndustry(ind.id)}
                    className={`p-3 rounded-xl border text-left text-sm transition-all ${
                      industry === ind.id
                        ? 'border-cyan-500 bg-cyan-500/10 text-white'
                        : 'border-slate-700 text-slate-300 hover:border-slate-600'
                    }`}
                    data-testid={`onboarding-industry-${ind.id}`}
                  >
                    {ind.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Step 1: Goal ── */}
          {step === 1 && (
            <div data-testid="onboarding-step-goal">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-cyan-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-cyan-400" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Hva er målet?</h1>
                <p className="text-slate-400">Velg hva slags system du vil bygge opp.</p>
              </div>

              <div className="space-y-3">
                {[
                  { id: 'internkontroll', name: 'Internkontroll (IK §5)', desc: 'Lovpålagt HMS-system for alle norske virksomheter' },
                  { id: 'iso', name: 'ISO-klargjøring', desc: 'ISO 9001, 14001 og/eller 45001 sertifisering' },
                  { id: 'miljofyrtarn', name: 'Miljøfyrtårn', desc: 'Norges mest brukte miljøsertifisering' },
                ].map(m => (
                  <button
                    key={m.id}
                    onClick={() => setMode(m.id)}
                    className={`w-full p-5 rounded-xl border text-left transition-all ${
                      mode === m.id
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : 'border-slate-700 hover:border-slate-600'
                    }`}
                    data-testid={`onboarding-mode-${m.id}`}
                  >
                    <div className="font-semibold text-white">{m.name}</div>
                    <div className="text-sm text-slate-400 mt-1">{m.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Step 2: Details (ISO standards / MF bransje) ── */}
          {step === 2 && (
            <div data-testid="onboarding-step-details">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-cyan-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Settings className="w-8 h-8 text-cyan-400" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  {mode === 'iso' ? 'Velg ISO-standarder' : mode === 'miljofyrtarn' ? 'Velg bransjekriterier' : 'Antall ansatte'}
                </h1>
              </div>

              {mode === 'iso' && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-400 mb-4">Hvilke ISO-standarder skal du klargjøre for?</p>
                  {ISO_STANDARDS.map(std => (
                    <button
                      key={std.id}
                      onClick={() => toggleIso(std.id)}
                      className={`w-full p-4 rounded-xl border text-left flex items-center justify-between transition-all ${
                        isoStandards.includes(std.id) ? 'border-cyan-500 bg-cyan-500/10' : 'border-slate-700 hover:border-slate-600'
                      }`}
                      data-testid={`onboarding-iso-${std.id}`}
                    >
                      <div>
                        <span className="font-semibold text-white">{std.name}</span>
                        <span className="text-sm text-slate-400 ml-2">— {std.desc}</span>
                      </div>
                      {isoStandards.includes(std.id) && <CheckCircle2 className="w-5 h-5 text-cyan-400" />}
                    </button>
                  ))}
                </div>
              )}

              {mode === 'miljofyrtarn' && (
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  <p className="text-sm text-slate-400 mb-4">Hvilken MF-bransje passer best?</p>
                  {MF_BRANSJER.map(b => (
                    <button
                      key={b.id}
                      onClick={() => setMfBransje(b.id)}
                      className={`w-full p-3 rounded-xl border text-left text-sm transition-all ${
                        mfBransje === b.id ? 'border-emerald-500 bg-emerald-500/10 text-white' : 'border-slate-700 text-slate-300 hover:border-slate-600'
                      }`}
                      data-testid={`onboarding-mf-${b.id}`}
                    >
                      {b.name}
                    </button>
                  ))}
                </div>
              )}

              {mode === 'internkontroll' && (
                <div>
                  <p className="text-sm text-slate-400 mb-4">Hvor mange ansatte har virksomheten?</p>
                  <input
                    type="number"
                    placeholder="f.eks. 15"
                    value={employees}
                    onChange={e => setEmployees(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white text-lg"
                    data-testid="onboarding-employees"
                  />
                </div>
              )}

              {/* Employee count for all modes */}
              {mode !== 'internkontroll' && (
                <div className="mt-6 pt-6 border-t border-slate-800">
                  <label className="text-sm text-slate-400 mb-2 block">Antall ansatte</label>
                  <input
                    type="number"
                    placeholder="f.eks. 15"
                    value={employees}
                    onChange={e => setEmployees(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white"
                    data-testid="onboarding-employees"
                  />
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Roles ── */}
          {step === 3 && (
            <div data-testid="onboarding-step-roles">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-cyan-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-cyan-400" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Roller i virksomheten</h1>
                <p className="text-slate-400">Velg aktuelle roller, eller legg til egne.</p>
              </div>

              <RolePicker
                selectedRoles={selectedRoles}
                onRolesChange={setSelectedRoles}
                variant="dark"
              />
            </div>
          )}

          {/* ── Step 4: Generate ── */}
          {step === 4 && (
            <div data-testid="onboarding-step-generate">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-cyan-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Rocket className="w-8 h-8 text-cyan-400" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  {done ? 'Alt klart!' : generating ? 'Genererer dokumenter...' : 'Klar til å generere'}
                </h1>
              </div>

              {!generating && !done && (
                <>
                  <div className="bg-slate-800/60 rounded-xl p-5 mb-6 space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-400">Bransje</span><span className="text-white">{INDUSTRIES.find(i => i.id === industry)?.name}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">Mål</span><span className="text-white">{modeLabels[mode]}</span></div>
                    {mode === 'miljofyrtarn' && mfBransje && (
                      <div className="flex justify-between"><span className="text-slate-400">MF-bransje</span><span className="text-white">{MF_BRANSJER.find(b => b.id === mfBransje)?.name}</span></div>
                    )}
                    <div className="flex justify-between"><span className="text-slate-400">Roller</span><span className="text-white">{selectedRoles.length} valgt</span></div>
                    {employees && <div className="flex justify-between"><span className="text-slate-400">Ansatte</span><span className="text-white">{employees}</span></div>}
                  </div>

                  <button
                    onClick={startGeneration}
                    className="w-full bg-gradient-to-r from-cyan-600 to-cyan-500 text-white font-semibold py-4 rounded-xl hover:from-cyan-500 hover:to-cyan-400 transition-all"
                    data-testid="onboarding-generate-btn"
                  >
                    Generer dokumenter nå
                  </button>
                </>
              )}

              {generating && !done && (
                <div className="space-y-4">
                  <div className="w-full bg-slate-800 rounded-full h-3 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full transition-all duration-1000"
                      style={{ width: `${progress}%` }}
                      data-testid="onboarding-progress-bar"
                    />
                  </div>
                  <div className="flex items-center justify-center gap-3 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">
                      {progress < 20 ? 'Forbereder...' :
                       progress < 50 ? 'Genererer dokumenter med AI...' :
                       progress < 90 ? 'Bygger prosedyrer og rutiner...' :
                       'Fullfører...'}
                    </span>
                  </div>
                </div>
              )}

              {done && (
                <div className="text-center space-y-4">
                  <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                  </div>
                  <p className="text-slate-300">Dokumentene dine er klare. Gå til dashboardet for å se dem.</p>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full bg-gradient-to-r from-cyan-600 to-cyan-500 text-white font-semibold py-4 rounded-xl hover:from-cyan-500 hover:to-cyan-400 transition-all"
                    data-testid="onboarding-go-dashboard"
                  >
                    Gå til dashboardet
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Navigation ── */}
          {step < 4 && (
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-800">
              {step > 0 ? (
                <button onClick={() => setStep(s => s - 1)} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm" data-testid="onboarding-back">
                  <ChevronLeft className="w-4 h-4" /> Tilbake
                </button>
              ) : <div />}

              <div className="flex items-center gap-3">
                <button onClick={skipOnboarding} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-300 text-sm" data-testid="onboarding-skip">
                  <SkipForward className="w-4 h-4" /> Hopp over
                </button>
                <button
                  onClick={() => setStep(s => s + 1)}
                  disabled={!canProceed()}
                  className="flex items-center gap-2 bg-cyan-600 text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  data-testid="onboarding-next"
                >
                  Neste <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {step === 4 && !generating && !done && (
            <div className="flex justify-between mt-6 pt-6 border-t border-slate-800">
              <button onClick={() => setStep(3)} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm" data-testid="onboarding-back">
                <ChevronLeft className="w-4 h-4" /> Tilbake
              </button>
              <button onClick={skipOnboarding} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-300 text-sm" data-testid="onboarding-skip">
                <SkipForward className="w-4 h-4" /> Hopp over
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
